import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { dateIntervalOptions, getItemTypeColors } from 'components/reporting/utils';
import { Options, SeriesOptionsType } from 'highcharts/highstock';
import {
    getNetPositionHeatMapData,
    getReportInfo,
    getReportItemData,
    getReportTypes,
    getSeriesFromReportChartSpec,
    getTimeSeriesData,
} from 'services/reporting';
import { colors } from 'theme/colors';
import {
    DateInterval,
    HeatMapData,
    QuickPickSeries,
    ReportItem,
    ReportItemTypeInfo,
    SeriesTimePoint,
    StandardReportInfo,
    StandardReportType,
} from 'types/reporting';

export const reportsApi = createApi({
    reducerPath: 'reportsApi',
    baseQuery: fetchBaseQuery({ baseUrl: '/reports' }),
    endpoints: (builder) => ({
        getReportTypes: builder.query<StandardReportType[], { studyId: number }>({
            queryFn: async ({ studyId }) => {
                try {
                    const result = await getReportTypes(studyId);
                    return { data: result };
                } catch (error) {
                    return { error: error.message || 'Failed to fetch reports' };
                }
            },
        }),
        getReportInfo: builder.query<StandardReportInfo, { studyId: number; reportType: StandardReportType }>({
            queryFn: async ({ studyId, reportType }) => {
                try {
                    const result = await getReportInfo(studyId, reportType);
                    return { data: result };
                } catch (error) {
                    return { error: error.message || 'Failed to fetch report charts' };
                }
            },
        }),
        getInfoItemsSeriesOptions: builder.query<
            SeriesOptionsType[],
            { info: StandardReportInfo; reportType: StandardReportType; quickPickSeries: QuickPickSeries }
        >({
            queryFn: async ({ info, reportType, quickPickSeries }) => {
                try {
                    const seriesData: Promise<{ item: ReportItem; data: SeriesTimePoint[] }>[] = [];
                    for (const item of info.items) {
                        seriesData.push(
                            getReportItemData(item, info.studyId, reportType, {
                                id: item.itemId,
                                quickPickSeries: quickPickSeries,
                            })
                        );
                    }

                    const allData = await Promise.all(seriesData);

                    const itemTypeColors = getItemTypeColors(info);

                    const series: SeriesOptionsType[] =
                        allData?.map((d) => ({
                            type: 'column',
                            name: d.item.name || `Item ${d.item.itemId}`,
                            color: itemTypeColors[d.item.itemTypeId].shift() || colors.gray[500],
                            legendIndex: 1,
                            data: d.data,
                        })) ?? [];

                    return { data: series };
                } catch (error) {
                    return { error: error.message || 'Failed to fetch report time series data' };
                }
            },
        }),
        getQuickPickSeries: builder.query<
            SeriesTimePoint[],
            { info: StandardReportInfo; reportType: StandardReportType; quickPickSeries: QuickPickSeries }
        >({
            queryFn: async ({ info, reportType, quickPickSeries }) => {
                try {
                    const data = await getTimeSeriesData(info.studyId, reportType, {
                        quickPickSeries: quickPickSeries,
                    });

                    return { data: data };
                } catch (error) {
                    return { error: error.message || 'Failed to fetch report time series data' };
                }
            },
        }),

        getChartFromReportSpec: builder.query<
            Options,
            {
                spec: ReportItemTypeInfo;
                studyId: number;
                itemId: number;
                reportType: StandardReportType;
                dateInterval: DateInterval;
            }
        >({
            queryFn: async ({ spec, studyId, itemId, reportType, dateInterval }) => {
                try {
                    const intervalLabel = dateIntervalOptions.find((d) => d.value === dateInterval)?.label || '';
                    // eslint-disable-next-line no-new-func
                    const title = new Function('dateInterval', `return \`${spec.title}\`;`)(intervalLabel);
                    const series = await getSeriesFromReportChartSpec(spec, studyId, itemId, reportType, dateInterval);
                    const options: Options = {
                        title: { text: title },
                        series: series,
                    };
                    return { data: options };
                } catch (error) {
                    return { error: error.message || 'Failed to fetch project net revenue data' };
                }
            },
        }),
        getNetPositionHeatMap: builder.query<
            HeatMapData,
            { studyId: number; itemId: number; year: string; reportType: StandardReportType }
        >({
            queryFn: async ({ studyId, itemId, year, reportType }) => {
                try {
                    const data = await getNetPositionHeatMapData(studyId, reportType, itemId);
                    const result = data[year] || [];
                    return { data: result };
                } catch (error) {
                    return { error: error.message || 'Failed to fetch heat map data' };
                }
            },
        }),
    }),
});

export const useGetReportTypes = reportsApi.endpoints.getReportTypes.useQuery;
export const useGetReportInfo = reportsApi.endpoints.getReportInfo.useQuery;
export const useGetInfoItemsSeriesOptions = reportsApi.endpoints.getInfoItemsSeriesOptions.useQuery;
export const useGetQuickPickSeries = reportsApi.endpoints.getQuickPickSeries.useQuery;
export const useGetChartFromReportSpec = reportsApi.endpoints.getChartFromReportSpec.useQuery;
export const useGetNetPositionHeatMap = reportsApi.endpoints.getNetPositionHeatMap.useQuery;
